import React from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { designColors, StatCard } from "services/constants/designTokens";

// Same accent/tint mapping as LeaveStatusCards.js — Pending/Approved/
// Rejected/Cancelled read the same color everywhere in Team Zone. Off-Day
// Duty rows merge two backend sources (Pre-Approval + Post-Approval) but
// both use this same four-value status vocabulary.
const STATUS_CARD_DEFS = [
  {
    key: "Pending",
    label: "Pending",
    icon: AccessTimeIcon,
    accent: "#F97316",
    tint: "#FFF7ED",
  },
  {
    key: "Approved",
    label: "Approved",
    icon: CheckCircleOutlineIcon,
    accent: "#16A34A",
    tint: "#EAFBF1",
  },
  {
    key: "Completed",
    label: "Completed",
    icon: DoneAllIcon,
    accent: "#1D4ED8",
    tint: "#EFF8FF",
  },
  {
    key: "Rejected",
    label: "Rejected",
    icon: HighlightOffIcon,
    accent: "#EF4444",
    tint: "#FEF2F2",
  },
  {
    key: "Cancelled",
    label: "Cancelled",
    icon: RemoveCircleOutlineIcon,
    accent: designColors.slate600,
    tint: designColors.slate100,
  },
];

const ALL_CARD_DEF = {
  key: "All",
  label: "All",
  icon: ListAltIcon,
  accent: "#1D4ED8",
  tint: "#EEF4FF",
};

// Bucket the API's `status` string into one of the four canonical statuses
// above (case/spelling tolerant), leaving anything unexpected untouched so
// an unrecognised status never silently disappears from the total.
export const canonicalOffDayDutyStatus = (status) => {
  const key = (status || "").trim().toLowerCase();
  if (key === "completed") return "Completed";
  if (key === "approved") return "Approved";
  if (key === "pending") return "Pending";
  if (key === "rejected") return "Rejected";
  if (key === "cancelled" || key === "canceled") return "Cancelled";
  return status;
};

// An Approved request whose date has already passed reads as "Completed" --
// same rule My Workspace's own Off-Day Duty screen already applies
// (MyZoneApply/OffDayDuty/index.js's resolveDisplayStatus), just never
// ported to this Team Workspace screen. `dateStr` is the row's own "date"
// field, already formatted server-side as "DD-MM-YYYY".
export const resolveOffDayDutyDisplayStatus = (status, dateStr) => {
  const canonical = canonicalOffDayDutyStatus(status);
  if (
    canonical === "Approved" &&
    dateStr &&
    moment(dateStr, "DD-MM-YYYY", true).isBefore(moment(), "day")
  ) {
    return "Completed";
  }
  return canonical;
};

// Single source of truth for a status's accent/tint pair, shared by these
// cards and DetailPanel's header chip.
export const offDayDutyStatusVisual = (status) => {
  const canonical = canonicalOffDayDutyStatus(status);
  return (
    STATUS_CARD_DEFS.find((card) => card.key === canonical) || {
      key: canonical,
      accent: designColors.slate600,
      tint: designColors.slate100,
    }
  );
};

export const OffDayDutyStatusCards = ({ counts, activeFilter, onCardClick }) => {
  const totalCount = Object.values(counts || {}).reduce(
    (sum, count) => sum + (count || 0),
    0,
  );

  return (
    <Mui.Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        "@media (max-width:599.95px)": {
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
        },
        gap: 1.5,
        mb: 2,
      }}
    >
      {[ALL_CARD_DEF, ...STATUS_CARD_DEFS].map((card) => {
        const isAll = card.key === "All";
        return (
          <StatCard
            activeBorder
            key={card.key}
            icon={card.icon}
            title={card.label}
            value={isAll ? totalCount : counts?.[card.key] || 0}
            accent={card.accent}
            tint={card.tint}
            isActive={isAll ? !activeFilter : activeFilter === card.key}
            onClick={onCardClick ? () => onCardClick(card.key) : undefined}
          />
        );
      })}
    </Mui.Box>
  );
};
